import React, { useState, useCallback, useEffect } from 'react';
import DesktopDiagnose from './DesktopView/DesktopDiagnose';
import MobileDiagnose from './MobileView/MobileDiagnose';
import ManualWrite from './ManualWrite/ManualWrite';
import AddedIssues from './AddedIssues/AddedIssues';
import * as css from './WriteYourOwn.module.scss';
import TextInCircle from '../TextInCircle/TextInCircle';
import { IView, ScrollBar } from '@framework-controls/core';
import { OpType, SpecData, ApptOps, UpdateApptOpsInp, DeteremineDeptInp, ascPageviewInput, ascCtaInteractionInput, VehicleRec, ascFormEngagementInput, ascElementConfigurationInput } from 'src/models';
import { strings } from 'src/localization';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { ascCtaInteraction, ascElementConfiguration, ascFormEngagement, ascPageview } from 'src/helpers/EventHelper/EventHelper';
import { ComponentMapIds } from '../map';


export interface Diagnoses {
    id: string,
    diagnosis: string,
    comment: string,
    opType: number,
}

export interface IndexMapInterface {
    index: string,
    hidden: boolean,
}

export interface IProps {
    diagnoses: Diagnoses[];
    specData?: SpecData;
    addDiagnosis: (diagnosis: Diagnoses) => void;
    removeDiagnosis: (id: string, e: any) => void;
    addLogEntry: (action: string, screen: string, details: string) => void;
    theme: string;
    specDefaultDept: string;
    unenteredIssue: string;
    unenteredDiagnosis: boolean;
    unenteredWarning: boolean;
    apptOps: ApptOps[];
    boxOpenFlag: boolean;
    mainPaneViewOrder: IView[];
    vehicles: VehicleRec[];
    selectedVehicle?: string;
    setBoxOpenFlag: (boolean: boolean) => void;
    setUnenteredWarning: (boolean: boolean) => void;
    setUnenteredIssue: (issue: string) => void;
    setUnenteredDiagnosis: (boolean: boolean) => void;
    UpdateApptOps: (inputOps: UpdateApptOpsInp[]) => void;
    determineDept: (inp: DeteremineDeptInp) => void;
    getDeptSpecs: () => void;
    warningProceed: () => void;
}

const WriteYourOwn = (props: IProps) => {
    // Manages the ManualWrite text field
    const [manualIssue, setManualIssue] = useState('');
    // A map of the currently selected dialog tree path
    const [indexMap, setIndexMap] = useState<IndexMapInterface[]>([]);
    // Tracks if the user confirmed they want to add an unentered Issue
    const [addUnenteredIssue, setAddUnenteredIssue] = useState<boolean>(false);
    // Manages title of unentered Diagnosis Tool issue
    const [unenteredTitle, setUnenteredTitle] = useState<string>('');
    // Manages comments for unenetered Diagnosis Tool
    const [unenteredComments, setUnenteredComments] = useState<string>('');
    // Triggers reset of comments and selected button in Diagnostic Tool if in mobile
    const [resetMobileDiagnose, setResetMobileDiagnose] = useState<boolean>(false);
    // Tracks if the user has chosen to add an unentered issue from the Diagnostic Tool
    const [addUnenteredDiagnosis, setAddUnenteredDiagnosis] = useState<boolean>(false);

    const addDiagnosis = useCallback((title: string, additionalComments: string, diagnosticOpType?: boolean) => {
        let opType = OpType.Diagnosis;
        /* Covers all the Diagnose tree. Other: prevents saving if nothing entered. Option selected w/ or w/o additonal comments. */
        if (title === '' || !(/\S/.test(title))) {
            return;
        } else if (manualIssue.length > 0 && /\S/.test(manualIssue)) {
            // this prevents saving Write your own if nothing entered.
            setManualIssue('');
            opType = OpType.WriteYourOwn;
        }
        if (diagnosticOpType) {
            opType = OpType.Diagnosis;
        }
        props.addDiagnosis(
            {
                id: '',
                diagnosis: title,
                comment: additionalComments === title ? '' : additionalComments,
                opType: opType,
            },
        );
        setIndexMap([]);
        props.setUnenteredIssue('');
        const ascFormEngagementInp: ascFormEngagementInput = {
            pageType: 'service',
            commType: 'form',
            commStatus: 'engage',
            elementText: opType === 0 ? strings.AddIssueButton : strings.AddDiagnosisButton,
            department: 'service',
            formName: 'Service_Portal_2',
            formType: 'service_appointment',
        };
        ascFormEngagement(ascFormEngagementInp);
        const ascCtaInteractionInp : ascCtaInteractionInput = {
            pageType: 'service',
            elementText: opType === 0 ? strings.AddIssueButton : strings.AddDiagnosisButton,
            elementColor: '',
            elementOrder: '',
            elementType: 'scheduling_tool',
            elementSubType: opType === 0 ? 'other_buttons' : 'nav_button',
            eventAction: 'click',
            eventActionResult: 'check',
            linkUrl: '',
        };
        ascCtaInteraction(ascCtaInteractionInp, title, props.vehicles, props.selectedVehicle);
        const ascElementConfigurationInp: ascElementConfigurationInput = {
            pageType: 'service',
            elementText: opType === 0 ? strings.AddIssueButton : strings.AddDiagnosisButton,
            elementType: 'scheduling_tool',
            elementColor: '',
            eventAction: 'click',
            eventActionResult: 'check',
            linkUrl: '',
        };
        ascElementConfiguration(ascElementConfigurationInp, title);
    }, [manualIssue, props.addDiagnosis, props.setUnenteredIssue, props.vehicles, props.selectedVehicle]);

    const addTwoWriteYourOwn = useCallback((diagnosis: Diagnoses, diagnosis2: Diagnoses) => {
        let writeYourOwnCount = 0;
        props.apptOps.forEach((apptOp) => {
            if (apptOp.opType === OpType.WriteYourOwn || apptOp.opType === OpType.Diagnosis) {
                writeYourOwnCount++;
            }
        });
        const writeYourOwnCount2 = writeYourOwnCount + 1;
        const apptOpsCopy: UpdateApptOpsInp[] = [
            ...props.apptOps,
            {
                id: 'diagnosis' + writeYourOwnCount,
                operation: diagnosis.opType === OpType.Diagnosis ? 'DIAG' : 'WYO',
                // operation: diagnosis.comment,
                concern: diagnosis.diagnosis + ' ' + diagnosis.comment,
                estTime: '',
                menuID: '',
                spgOp: '',
                opType: diagnosis.opType,
                // department: 'S',
                department: props.specDefaultDept,
                itemCostLow: '****',
                itemCostHigh: '****',
                opWaiter: '',
                opLoaner: '',
                opRental: '',
                opConcierge: '',
                opCadillac: '',
                opPPUDWL: '',
            },
            {
                id: 'diagnosis' + writeYourOwnCount2,
                operation: diagnosis2.opType === OpType.Diagnosis ? 'DIAG' : 'WYO',
                // operation: diagnosis.comment,
                concern: diagnosis2.diagnosis + ' ' + diagnosis2.comment,
                estTime: '',
                menuID: '',
                spgOp: '',
                opType: diagnosis2.opType,
                // department: 'S',
                department: props.specDefaultDept,
                itemCostLow: '****',
                itemCostHigh: '****',
                opWaiter: '',
                opLoaner: '',
                opRental: '',
                opConcierge: '',
                opCadillac: '',
                opPPUDWL: '',
            },
        ];
        props.UpdateApptOps(apptOpsCopy);
        props.setUnenteredIssue('');
        setManualIssue('');
        setIndexMap([]);
        props.setUnenteredDiagnosis(false);
        setResetMobileDiagnose(true);
        const defaultDept = props.specDefaultDept;
        const determineDept = {
            defaultDept: defaultDept,
        };
        props.determineDept(determineDept);
        props.getDeptSpecs();
    }, [props.apptOps, props.specDefaultDept, props.UpdateApptOps, props.setUnenteredIssue,
        props.setUnenteredDiagnosis, props.determineDept, props.getDeptSpecs]);

    // Toggles a previously selected option along the index map to hidden or displayed
    const toggleDisplay = useCallback((index: number) => {
        const indexMapCopy = [...indexMap];
        indexMapCopy[index].hidden = !indexMapCopy[index].hidden;
        setIndexMap(indexMapCopy);
    }, [indexMap]);

    // When selecting a new option update the indexMap
    const selectButton = useCallback((value: string) => {
        setIndexMap([
            ...indexMap,
            {
                index: value,
                hidden: true,
            },
        ]);
    }, [indexMap]);

    // Pop down to the level of the changed option, then push the newly selected option
    const selectPrevButton = useCallback((index: string, selected: string) => {
        const indexMapCopy = [...indexMap];
        const changedIndex = parseInt(index);
        if (!isNaN(changedIndex)) {
            while (indexMapCopy.length > changedIndex) {
                indexMapCopy.pop();
            }
            indexMapCopy.push({ index: selected, hidden: false });
            setIndexMap(indexMapCopy);
        }
    }, [indexMap]);

    const backButton = useCallback(() => {
        const indexMapCopy = [...indexMap];
        indexMapCopy.pop();
        setIndexMap(indexMapCopy);
    }, [indexMap]);

    const nextButton = useCallback((selectedButton: string) => {
        const indexMapCopy = [...indexMap];
        indexMapCopy.push({ index: selectedButton, hidden: false });
        setIndexMap(indexMapCopy);
    }, [indexMap]);

    const clearIndexMap = useCallback(() => {
        setIndexMap([]);
    }, []);

    const closeUnenteredHandler = () => {
        props.setUnenteredWarning(false);
    };

    const addUnenteredIssueHandler = () => {
        if (props.unenteredIssue && props.unenteredDiagnosis) {
            const diagnosis1 = {
                id: '',
                diagnosis: unenteredTitle && unenteredTitle !== `Describe your vehicle's issue:` ?
                    unenteredTitle : unenteredComments,
                comment: unenteredComments && unenteredTitle !== `Describe your vehicle's issue:` ? unenteredComments : '',
                opType: OpType.Diagnosis,
            };
            const diagnosis2 = {
                id: '',
                diagnosis: props.unenteredIssue,
                comment: '',
                opType: OpType.WriteYourOwn,
            };
            addTwoWriteYourOwn(diagnosis1, diagnosis2);
            props.setUnenteredIssue('');
            setAddUnenteredIssue(false);
            props.setUnenteredDiagnosis(false);
            props.warningProceed();
        }
        if (props.unenteredIssue && !props.unenteredDiagnosis) {
            setAddUnenteredIssue(true);
            props.warningProceed();
        }
        if (props.unenteredDiagnosis && !props.unenteredIssue) {
            setAddUnenteredDiagnosis(true);
            setResetMobileDiagnose(true);
            props.warningProceed();
        }
        props.setUnenteredWarning(false);
        props.setBoxOpenFlag(false);
    };

    const checkUnentered = () => {
        if (props.unenteredWarning && (props.unenteredIssue || props.unenteredDiagnosis)){
            return <ErrorMessage
                headerMsg={props.specData?.writeYourOwnTabMsg ? props.specData?.writeYourOwnTabMsg : ''}
                errorMsg={strings.EnteredIssueError}
                primaryBtnMessage={strings.Yes}
                secondaryBtnMessage={strings.Cancel}
                primaryHandler={addUnenteredIssueHandler}
                secondaryHandler={closeUnenteredHandler}
                closeHandler={closeUnenteredHandler}
            />;
        } else {
            return null;
        }
    };

    const checkForUnenteredIssue = checkUnentered();

    useEffect(() => {
        const currentPane = props.mainPaneViewOrder[props.mainPaneViewOrder.length - 1];
        if (currentPane.viewId === ComponentMapIds.ServiceTab) {
            const ascPageViewInp: ascPageviewInput = {
                pageType: 'service',
                errorCode: '',
                itemResults: '',
            };
            ascPageview(ascPageViewInp, props.vehicles, props.selectedVehicle);
        }
    }, [props.mainPaneViewOrder, props.vehicles, props.selectedVehicle]);

    useEffect(() => {
        props.addLogEntry('Screen Entry', 'Write Your Own', '');
    }, [
        props.addLogEntry,
    ]);

    // Track Manual Issue input
    useEffect(() => {
        manualIssue ? props.setUnenteredIssue(manualIssue) : props.setUnenteredIssue('');
    }, [manualIssue, props.setUnenteredIssue, props.unenteredIssue]);

    // Add unentered issue if user chooses Yes from Unentered Issue Warning
    useEffect(() => {
        if (addUnenteredIssue) {
            addDiagnosis(props.unenteredIssue, '', false);
            setAddUnenteredIssue(false);
        }
    }, [addDiagnosis, addUnenteredIssue, props.unenteredIssue]);

    return (
        <ScrollBar scrollVertical>
            {checkForUnenteredIssue}
            <div className={css.body}>
                {props.diagnoses.length > 0 ? <AddedIssues
                    IssueList={props.diagnoses}
                    removeIssue={(id, e) => props.removeDiagnosis(id, e)}/>: null }
                <div className={css.writeYourOwn}>
                    {props.specData?.showDiagnTool ?
                        <React.Fragment>
                            <DesktopDiagnose
                                addDiagnosis={addDiagnosis}
                                indexMap={indexMap}
                                selectButton={selectButton}
                                backButton={backButton}
                                nextButton={nextButton}
                                clearIndexMap={clearIndexMap}
                                title={props.specData.diagnToolMsg}
                                theme={props.theme}
                                vehicles={props.vehicles}
                                selectedVehicle={props.selectedVehicle}
                            />
                            <MobileDiagnose
                                addDiagnosis={addDiagnosis}
                                indexMap={indexMap}
                                toggleDisplay={toggleDisplay}
                                selectButton={selectButton}
                                selectPrevButton={selectPrevButton}
                                title={props.specData.diagnToolMsg}
                                unenteredDiagnosis={props.unenteredDiagnosis}
                                setUnenteredDiagnosis={props.setUnenteredDiagnosis}
                                addUnenteredDiagnosis={addUnenteredDiagnosis}
                                setAddUnenteredDiagnosis={setAddUnenteredDiagnosis}
                                setUnenteredTitle={setUnenteredTitle}
                                setUnenteredComments={setUnenteredComments}
                                resetMobileDiagnose={resetMobileDiagnose}
                                setResetMobileDiagnose={setResetMobileDiagnose}
                                theme={props.theme}
                            />
                        </React.Fragment>: null}
                    {props.specData?.showDiagnTool && props.specData?.showWrtYourOwn ?
                        <div className={css.circleText}>
                            <TextInCircle text={strings.OrLiteral} theme={props.theme} />
                        </div> : null}
                    {props.specData?.showWrtYourOwn ? <ManualWrite
                        className={css.flexbox}
                        vehicles={props.vehicles}
                        selectedVehicle={props.selectedVehicle}
                        addDiagnosis={addDiagnosis}
                        manualIssue={manualIssue}
                        setManualIssue={setManualIssue}
                        theme={props.theme}
                    /> : null}
                </div>
            </div>
        </ScrollBar>
    );
};

export default WriteYourOwn;